import { Draw } from "ol/interaction";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { TagsHandler } from ".";
import { setAerialTooltip } from "../../viewer";
import { createBox, createRegularPolygon, GeometryFunction } from "ol/interaction/Draw";
import { Circle, Fill, Stroke, Style } from "ol/style";
import { Feature } from "ol";
import { CustomMouseEvent } from "../controls";
import { t } from "../../../localization";
import { AnnotationDrawType, AnnotationSize } from "../../../types/tags";
import { nanoid } from "@reduxjs/toolkit";
import { TagAnnotation } from "../../../redux/assets-slice";
import { withOpacity } from "../utilities";
import GeoJSON from 'ol/format/GeoJSON';
import { Type } from "ol/geom/Geometry";
import { Polygon } from "ol/geom";

const format = new GeoJSON();

export class TagAnnotations {
  private tagsHandler: TagsHandler;
  private type: AnnotationDrawType;
  private draw: Draw;
  private sketch: Feature;
  public enabled = false;
  public drawing = false;
  private hitTolerance = 10;
  private color = "#FF0000";
  private size = AnnotationSize.Medium;
  private annotations: TagAnnotation[] = []
  private mapLayer: VectorLayer<any>;
  private mapSource: VectorSource;

  constructor(tagsHandler: TagsHandler) {
    this.tagsHandler = tagsHandler;

    this.mapSource = new VectorSource({
      wrapX: false,
    });

    this.mapLayer = new VectorLayer({
      source: this.mapSource,
      style: feature => {
        const color = feature.get("color") as string;
        const size = feature.get("size") as number;
        return this.getLayerStyle(color, size, false);
      }
    });
  }

  get erasing() {
    return this.type === AnnotationDrawType.Eraser;
  }

  get viewer() {
    return this.tagsHandler.viewer;
  }

  get minimap() {
    return this.viewer.minimap;
  }

  get map() {
    return this.minimap.map;
  }

  get startTooltip() {
    if (this.type === AnnotationDrawType.Eraser) {
      return [t('tags.click_to_erase_annotation')];
    }

    return [
      t('tags.click_to_start_annotation')
    ];
  }

  get moreTooltip() {
    const isShape = (this.type === AnnotationDrawType.Rectangle)
      || (this.type === AnnotationDrawType.Circle)
      || (this.type === AnnotationDrawType.Star);

    if (this.type === AnnotationDrawType.Polygon) {
      return [
        t('tags.click_to_add_more_points'),
        t('tags.double-click_to_finish'),
        t('tags.right-click_to_cancel')
      ];
    } else if (this.type === AnnotationDrawType.PolygonFreeHand) {
      return [
        t('tags.click_and_hold_to_draw_polygon')
      ];
    } else if (this.type === AnnotationDrawType.LineString) {
      return [
        t('tags.click_to_add_more_points'),
        t('tags.double-click_to_finish'),
        t('tags.right-click_to_cancel')
      ];
    } else if (this.type === AnnotationDrawType.LineStringFreeHand) {
      return [
        t('tags.click_and_hold_to_draw_line')
      ];
    } else if (isShape) {
      return [
        t('tags.click_to_finish'),
        t('tags.right-click_to_cancel')
      ];
    } else if (this.type === AnnotationDrawType.Eraser) {
      return [
        t('tags.click_to_erase_annotation')
      ];
    }

    return [];
  }

  setState(state: boolean) {
    if (state === this.enabled) return;
    this.enabled = state;

    if (state) {
      this.map.addLayer(this.mapLayer);
      this.mapSource.clear();
    } else {
      this.map.removeLayer(this.mapLayer);
      this.reset();
    }
  }

  reset() {
    this.drawing = false;
    this.removeInteraction();
    this.mapSource.clear();
    this.annotations = [];
  }

  load(annotations: TagAnnotation[]) {
    this.annotations = annotations ? annotations : [];

    const features = this.annotations.map(annotation => {
      const {definition} = annotation;
      const feature = format.readFeature(definition) as Feature;
      return feature;
    });

    this.mapSource.clear();
    this.mapSource.addFeatures(features);
  }

  getAnnotations() {
    return this.annotations;
  }

  /** @private */
  removeInteraction() {
    if (!this.draw) return;
    this.map.removeInteraction(this.draw);
    this.draw = null;
  }

  /** @private */
  addInteraction() {
    this.removeInteraction();

    if (this.type === AnnotationDrawType.Polygon) {
      this.draw = this.getDrawTool({type: "Polygon"});
    } else if (this.type === AnnotationDrawType.PolygonFreeHand) {
      this.draw = this.getDrawTool({type: "Polygon", freehand: true});
    } else if (this.type === AnnotationDrawType.LineString) {
      this.draw = this.getDrawTool({type: "LineString"});
    } else if (this.type === AnnotationDrawType.LineStringFreeHand) {
      this.draw = this.getDrawTool({type: "LineString", freehand: true});
    } else if (this.type === AnnotationDrawType.Circle) {
      this.draw = this.getDrawTool({
        type: "Circle",
        geometryFunction: createRegularPolygon()
      });
    } else if (this.type === AnnotationDrawType.Rectangle) {
      this.draw = this.getDrawTool({
        type: "Circle",
        geometryFunction: createBox()
      });
    } else if (this.type === AnnotationDrawType.Star) {
      this.draw = this.getDrawTool({
        type: "Circle",
        geometryFunction: starGeometryFunction
      });
    } else {
      this.draw = null;
    }

    if (!this.draw) return;

    this.map.addInteraction(this.draw);

    this.draw.on('drawstart', event => {
      this.sketch = event.feature;
    });

    this.draw.on('drawend', (event) => {
      this.onDrawEnd(event);
    });
  }

  onDrawEnd(event) {
    this.sketch = null;

    setAerialTooltip(this.startTooltip);

    const feature = event.feature;
    const annotationID = nanoid();
    feature.set("id", annotationID);
    feature.set("color", this.color);
    feature.set("size", this.size);

    const definition = format.writeFeatureObject(feature);

    const annotation = {
      id: annotationID,
      definition
    } as TagAnnotation;

    this.annotations.push(annotation);
  }

  setType(type: AnnotationDrawType) {
    this.type = type;
    if (!this.drawing) return;
    this.addInteraction();
  }

  setColor(color: string) {
    this.color = color;
    this.setType(this.type);
  }

  setSize(size: AnnotationSize) {
    this.size = size;
    this.setType(this.type);
  }

  setDrawing(drawing: boolean) {
    this.drawing = drawing;

    if (drawing) {
      this.addInteraction();
    } else {
      this.removeInteraction();
    }
  }

  getDrawTool(options: {
    type: Type,
    freehand?: boolean,
    geometryFunction?: GeometryFunction
  }) {
    const {type, freehand=false, geometryFunction} = options;

    const style = this.getLayerStyle(
      this.color, this.size, true);

    const condition = event => {
      return event.originalEvent.button === 0;
    };

    return new Draw({
      source: this.mapSource,
      type,
      freehand,
      condition,
      geometryFunction,
      style
    });
  }

  setLineStringTool(freehand: boolean) {
    const style = this.getLayerStyle(
      this.color, this.size, true);

    const condition = event => {
      return event.originalEvent.button === 0;
    };

    this.draw = new Draw({
      source: this.mapSource,
      type: "LineString",
      freehand,
      condition,
      style
    });
  }

  setRectangleTool() {
    const style = this.getLayerStyle(
      this.color, this.size, true);

    const condition = event => {
      return event.originalEvent.button === 0;
    };

    this.draw = new Draw({
      source: this.mapSource,
      type: "Circle",
      geometryFunction: createBox(),
      condition,
      style
    });
  }

  getLayerStyle(color: string, size: number, dashed: boolean) {
    const dash = dashed ? [10, 10] : undefined;
    const style =  new Style({
      fill: new Fill({
        color: withOpacity(color, 0.2)
      }),
      stroke: new Stroke({
        color: color,
        width: size,
        lineDash: dash
      }),
      image: new Circle({
        radius: size + 1,
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.7)'
        }),
        fill: new Fill({
          color: color
        })
      })
    });

    return style;
  }

  onMouseMove() {
    if (!this.enabled) return;

    const tooltip = this.sketch
      ? this.moreTooltip
      : this.startTooltip;

    setAerialTooltip(tooltip);
  }

  onMouseUp(event: CustomMouseEvent) {
    if (!this.enabled || !this.drawing) return false;

    if (event.isRightClick) {
      this.setType(this.type);
      return true;
    }

    return false;
  }

  onMapClick(event) {
    if (!this.enabled || !this.erasing) return false;

    const features = this.map.getFeaturesAtPixel(event.pixel, {
      layerFilter: (layer: any) =>  layer === this.mapLayer,
      hitTolerance: this.hitTolerance
    }) as Feature[];

    for (let feature of features) {
      // Remove from map source
      this.mapSource.removeFeature(feature);

      // Remove from annotations array
      const toRemove = feature.get("id");
      this.annotations = this.annotations
        .filter(annotation => annotation.id !== toRemove);
    }

    return features.length > 0;
  }
}

const starGeometryFunction = (coordinates, geometry) => {
  const center = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  const dx = center[0] - last[0];
  const dy = center[1] - last[1];
  const radius = Math.sqrt(dx * dx + dy * dy);
  const rotation = Math.atan2(dy, dx);
  const newCoordinates = [];
  const numPoints = 12;

  for (let i = 0; i < numPoints; ++i) {
    const angle = rotation + (i * 2 * Math.PI) / numPoints;
    const fraction = i % 2 === 0 ? 1 : 0.5;
    const offsetX = radius * fraction * Math.cos(angle);
    const offsetY = radius * fraction * Math.sin(angle);
    newCoordinates.push([center[0] + offsetX, center[1] + offsetY]);
  }
  newCoordinates.push(newCoordinates[0].slice());

  if (!geometry) {
    geometry = new Polygon([newCoordinates]);
  } else {
    geometry.setCoordinates([newCoordinates]);
  }

  return geometry;
};